import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Sparkles,
  Bot,
  Wallet,
  Users2,
  Search,
  Plane,
  CreditCard,
  Tv,
  Target as TargetIcon,
  Bell,
  ReceiptText,
  Crown,
  Home,
  LogOut,
  Menu,
  X,
  ListTree,
  BadgePercent,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/feed", label: "Smart Feed", icon: Sparkles },
  { to: "/app/ria", label: "Ria · AI Assistant", icon: Bot },
  { to: "/app/search", label: "Search Deals", icon: Search },
  { to: "/app/categories", label: "Categories", icon: ListTree },
  { to: "/app/wallet", label: "Wallet", icon: Wallet },
  { to: "/app/referrals", label: "Referrals", icon: Users2 },
  { to: "/app/family", label: "Family Vault", icon: Home },
  { to: "/app/subscriptions", label: "Subscriptions", icon: Tv },
  { to: "/app/travel", label: "Travel Optimizer", icon: Plane },
  { to: "/app/credit-cards", label: "Credit Card Match", icon: CreditCard },
  { to: "/app/goals", label: "Savings Goals", icon: TargetIcon },
  { to: "/app/alerts", label: "Alerts", icon: Bell },
  { to: "/app/receipts", label: "Receipts", icon: ReceiptText },
  { to: "/app/premium", label: "Premium", icon: Crown },
];

export default function ConsumerSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const initials =
    (user?.name || user?.email || "U").trim().slice(0, 1).toUpperCase();

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-72 bg-white border-r border-gray-200 transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="consumer-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-gray-200">
          <Link to="/app" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center">
              <BadgePercent className="w-5 h-5" />
            </div>
            <span className="font-display font-bold tracking-tight">LoyaltyCircle</span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-0.5 overflow-y-auto h-[calc(100vh-9rem)]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-800 font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 bg-emerald-700">
              <AvatarFallback className="bg-emerald-700 text-white font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <Link to="/app/profile" className="text-xs text-gray-500 hover:text-emerald-700">
                View profile
              </Link>
            </div>
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-red-600"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function ConsumerTopbar({ onMenu }) {
  return (
    <div className="lg:hidden sticky top-0 z-30 h-14 bg-white/80 backdrop-blur border-b border-gray-200 flex items-center justify-between px-4">
      <button
        onClick={onMenu}
        className="p-2 rounded-lg hover:bg-gray-100"
        data-testid="topbar-menu"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>
      <span className="font-display font-bold">LoyaltyCircle AI</span>
      <div className="w-9" />
    </div>
  );
}
